import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';

import '@arcgis/map-components/components/arcgis-map';
import * as intl from '@arcgis/core/intl.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import ImageryTileLayer from '@arcgis/core/layers/ImageryTileLayer';
import TileInfo from '@arcgis/core/layers/support/TileInfo';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import MapView from '@arcgis/core/views/MapView';
import LayerSearchSource from '@arcgis/core/widgets/Search/LayerSearchSource';
import LocatorSearchSource from '@arcgis/core/widgets/Search/LocatorSearchSource';
import * as locator from '@arcgis/core/rest/locator';
import { CommonModule } from '@angular/common';
import { PrimengModule } from 'src/app/modules/primeng.module';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ContextMenu } from 'primeng/contextmenu';
import {
  ARCGIS_MAP_LIFECYCLE,
  ArcgisMapLifecycle,
} from './constants/map-life-cycle.interface';
import { MapControlItem } from './constants/map-control-item.interface';
import { ShapeItem } from './constants/shape-item.interface';
import { ControlType } from './constants/control-type.enum';
import { GraphicHelperService } from './services/graphic-helper.service';
import { ControlFactoryService } from './services/control-factory.service';
import { SketchingService } from './services/sketching.service';
import { MenuItemsService } from 'src/app/services/menu-items.service';
import { LanguageService } from 'src/app/services/language.service';
import { AuthService } from 'src/app/services/auth.service';
import { Entity } from 'src/app/models/mvp-query-response-dto';
import { RoutingService } from 'src/app/services/routing.service';
import { LinkInfoDto } from 'src/app/models/link-info.dto';
import { createGushParcelPopupTemplate } from './popupTemplates/gushParcelPopupTemplate';
import { GushParcelSearchService } from 'src/app/services/gush-parcel-search.service';
import { HACHRAZA_DETAILS_URL } from 'src/app/constants';

@Component({
  selector: 'app-arcgis-map',
  templateUrl: './arcgis-map.component.html',
  styleUrl: './arcgis-map.component.scss',
  standalone: true,
  providers: [GraphicHelperService, ControlFactoryService, SketchingService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, PrimengModule],
})
export class ArcGisMapComponent implements OnDestroy, OnInit {
  @Input() mapControls: MapControlItem[] = [];
  @Input() currentZoom = 13;
  @Input() basemap = 'osm';
  @Input() extentAll = false;
  @Input() popupEnabled = true;
  @Input() selectedEntityId: number | null = null;
  // Pan to the selected shape at the current zoom instead of zooming to fit it.
  @Input() keepZoomOnSelect = false;
  @Input() enableSketching = false;
  @Input() sketchShapes: ShapeItem[] = [];
  @Input() autoExpandSearch = false;
  // Keep orthophoto COG layers above the shape layers even when shapes are redrawn
  // after the COG loaded (e.g. the model page, where the model polygon sits under its orthophoto).
  @Input() cogAboveShapes = false;

  @Output() updateSelectedEntitiesFromMap = new EventEmitter<Entity[]>();
  @Output() sketchShapesChanged = new EventEmitter<ShapeItem[]>();
  @Output() LeftClick = new EventEmitter<ShapeItem[]>();
  @Output() RightClick = new EventEmitter<ShapeItem[]>();
  @Output() DrawShapes = new EventEmitter<ShapeItem[]>();
  @Output() viewReady = new EventEmitter<MapView>(); // Emit when view is ready

  @ViewChild('contextMenu') contextMenu!: ContextMenu;
  @ViewChild('arcgismap') arcgismap!: ElementRef;

  private _shapes: ShapeItem[] = [];
  private shapesTimeout: any;
  private _mapView: MapView | undefined | null;
  private dynamicControls: any[] = [];
  private graphicsLayers = new Map<string, GraphicsLayer>();
  private cogLayers: ImageryTileLayer[] = [];
  private languageSubscription?: Subscription;
  private sketchSubscription?: Subscription;
  private clickedShape: ShapeItem | null = null;
  private mousePosition: { x: number; y: number } = { x: 0, y: 0 };
  private internalUser: boolean = false;
  private searchWidgetElement: any = null;
  private searchExpandElement: any = null;

  currentLang!: string;
  mapMenuItems: any[] = [];
  @Input() set shapes(shapes: ShapeItem[]) {
    this._shapes = shapes;
    this.debouncedSetShapes(shapes);
  }

  @Input() set lang(lang: 'he' | 'en') {
    intl.setLocale(lang ?? 'he');
  }

  constructor(
    @Inject(ARCGIS_MAP_LIFECYCLE) private lifecycleHooks: ArcgisMapLifecycle,
    private graphicHelper: GraphicHelperService,
    private factory: ControlFactoryService,
    private sketchingService: SketchingService,
    private menuItemsService: MenuItemsService,
    private languageService: LanguageService,
    private authService: AuthService,
    private translate: TranslateService,
    private routingService: RoutingService,
    private gushParcelSearchService: GushParcelSearchService,
    private ngZone: NgZone,
  ) {}
  ngOnInit(): void {
    this.initializeLanguageSubscription();
    this.initializeAuthSubscription();
  }

  private initializeLanguageSubscription(): void {
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(
      (language) => {
        this.currentLang = language;
        this.updateContextMenu();
        this.updateBasemapForLanguage(language);
        this.handleLanguageChangeForShapes(language);
        this.updateLayerTitles();
        this.setEsriDirectionAndLocale();
      },
    );
  }

  private initializeAuthSubscription(): void {
    this.authService.internalUser$.subscribe((isInternalUser) => {
      this.internalUser = isInternalUser;
      this.basemap = this.internalUser ? 'satellite' : 'osm';
      this.generateControls();
    });
  }

  private updateBasemapForLanguage(language: string): void {
    if (!this.internalUser && this._mapView) {
      this.basemap = language === 'en' ? 'streets-vector' : 'osm';
    }
  }

  private handleLanguageChangeForShapes(language: string): void {
    if (this._mapView && this._shapes.length > 0) {
      setTimeout(() => {
        const popupState = this.capturePopupState();
        this.recreateShapesWithUpdatedLanguage();
        this.restorePopupIfNeeded(popupState);
      }, 300);
    }
  }

  private capturePopupState() {
    return {
      wasOpen: this._mapView?.popup?.visible,
      location: this._mapView?.popup?.location?.clone(),
      wkt: this._mapView?.popup?.selectedFeature?.attributes?.shape?.wkt,
    };
  }

  private recreateShapesWithUpdatedLanguage(): void {
    this.clearAllGraphicsLayers();
    this.updateShapesPopupTemplates();
    this.addShapes(this._shapes);
  }

  private restorePopupIfNeeded(popupState: any): void {
    if (popupState.wasOpen && popupState.location && popupState.wkt) {
      setTimeout(() => {
        this.refreshOpenPopupByWKT(popupState.location, popupState.wkt);
      }, 200);
    }
  }
  ngOnDestroy(): void {
    if (this._mapView) {
      this.cleanupMapView();
    }
    this.cleanupTimeouts();
    this.cleanupSubscriptions();
  }

  private cleanupMapView(): void {
    this._shapes = [];
    this.cogLayers = [];
    this.lifecycleHooks.onViewDestroyed?.();
    this.cleanupGraphicsLayers();
    this.clearControls();
    this.sketchingService.destroy();
    this.destroyMapViewLayers();
    this._mapView!.container = null;
    // Wrap MapView destroy in try-catch to suppress expected AbortErrors
    try {
      this._mapView!.destroy();
    } catch (error) {
      // Silently handle expected AbortErrors from MapView cleanup
      if (error && (error as any).name !== 'AbortError') {
        console.warn('Non-critical MapView cleanup error:', error);
      }
    }
    this._mapView = null;
  }

  private cleanupGraphicsLayers(): void {
    this.graphicsLayers.forEach((layer) => layer.removeAll());
    this.graphicsLayers.clear();
  }

  private destroyMapViewLayers(): void {
    this._mapView!.map?.allLayers.forEach((layer) => {
      if (layer.type === 'graphics') {
        (layer as any).removeAll?.();
      }
      this._mapView!.map?.remove(layer);
      // Wrap destroy in try-catch to suppress expected AbortErrors from layer cleanup
      try {
        layer.destroy?.();
      } catch (error) {
        // Silently handle expected AbortErrors from layer cleanup
        if (error && (error as any).name !== 'AbortError') {
          console.warn('Non-critical layer cleanup error:', error);
        }
      }
    });
  }

  private cleanupTimeouts(): void {
    if (this.shapesTimeout) {
      clearTimeout(this.shapesTimeout);
    }
  }

  private cleanupSubscriptions(): void {
    this.languageSubscription?.unsubscribe();
    this.sketchSubscription?.unsubscribe();
  }

  async arcgisViewReadyChange(event: any) {
    const gisMap = (event.target as any).view as __esri.MapView;

    const baseLods = TileInfo.create({ spatialReference: new SpatialReference({ wkid: 102100 }) }).lods;
    const lastLod = baseLods[baseLods.length - 1];
    const extraLods: __esri.LOD[] = [];
    for (let i = 1; i <= 12; i++) {
      extraLods.push({
        level: lastLod.level + i,
        resolution: lastLod.resolution / Math.pow(2, i),
        scale: lastLod.scale / Math.pow(2, i),
        levelValue: String(lastLod.level + i),
      } as __esri.LOD);
    }
    gisMap.constraints = {
      lods: [...baseLods, ...extraLods],
      minScale: 0,
    };
    this._mapView = gisMap;
    this.lifecycleHooks.onViewReady?.(gisMap, this.basemap);

    // Emit the view so parent components can access it
    this.viewReady.emit(gisMap);

    await this.generateControls();
    this.setEsriDirectionAndLocale();
    if (this._mapView.popup) {
      // Import reactiveUtils at the top of your component
      const reactiveUtils = await import('@arcgis/core/core/reactiveUtils');

      reactiveUtils.on(
        () => this._mapView?.popup,
        'trigger-action',
        (event: any) => {
          this.handlePopupAction(event);
        },
      );

      // Watch for popup visibility changes to track selected entity
      reactiveUtils.watch(
        () => this._mapView?.popup?.visible,
        (visible) => {
          if (visible && this._mapView?.popup?.selectedFeature) {
            const shape = this._mapView.popup.selectedFeature.attributes?.shape;
            if (shape) {
              this.selectedEntityId = shape.shapeData?.entity?.entityId || null;
              this.clickedShape = shape;
              this.updateShapeHighlighting();
            }
          } else if (!visible) {
            // Clear selection when popup closes
            this.selectedEntityId = null;
            this.clickedShape = null;
          }
        },
      );
    }

    if (this.enableSketching) {
      this.initializeSketching();
    }

    this.initializeShapesAndHighlighting();

    if (this.enableSketching) {
      this.sketchingService.ensureSketchLayerOnTop();
    }
  }
// zoomToOrthophoto: jump to the first COG's extent once it loads. Pages that already frame
// their own shapes (the model page zooms to the model polygon) pass false.
public displayOrthophotoEntities(entities: Entity[], zoomToOrthophoto = true): void {
    this.clearCogLayers();
    if (!this._mapView) return;

    const orthoEntities = entities.filter(e => e.entityType === 'orthophoto');
    if (orthoEntities.length === 0) return;

    // Deduplicate by originEntityId
    const seen = new Set<string>();
    const unique = orthoEntities.filter(e => {
        if (seen.has(e.originEntityId!)) return false;
        seen.add(e.originEntityId!);
        return true;
    });

    const baseUrl = unique[0].imageUrl!.replace(/\/orthophoto\/.*$/, '');
    const ids = unique.map(e => Number(e.originEntityId));

    // Batch fetch all signed GCS URLs in one request
    fetch(`${baseUrl}/orthophoto/signed-urls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids)
    })
    .then(r => r.json())
    .then((signedUrls: Record<number, string>) => {
        let firstZoomed = !zoomToOrthophoto;

        for (const entity of unique) {
            const signedUrl = signedUrls[Number(entity.originEntityId)];
            if (!signedUrl) continue;

            const gcsBaseUrl = signedUrl.split('?')[0];
            const customParameters: Record<string, string> = {};
            const searchParams = new URLSearchParams(signedUrl.split('?')[1]);
            searchParams.forEach((value, key) => customParameters[key] = value);

            const cogLayer = new ImageryTileLayer({
                url: gcsBaseUrl,
                title: `Orthophoto ${entity.originEntityId}`,
                opacity: 1,
                customParameters
            });

            this.cogLayers.push(cogLayer);

            cogLayer.load()
                .then(async () => {
                    if (!firstZoomed && cogLayer.fullExtent) {
                        firstZoomed = true;
                        await this._mapView?.goTo(
                            cogLayer.fullExtent.expand(1.5),
                            { animate: false }
                        ).catch((e: any) => console.error('goTo failed:', e));
                    }
                    this._mapView?.map?.add(cogLayer);
                    this._mapView?.whenLayerView(cogLayer).then(async (layerView: any) => {
                        const reactiveUtils = await import('@arcgis/core/core/reactiveUtils');
                        await reactiveUtils.whenOnce(() => !layerView.updating).catch(() => {});
                        if (this._mapView) {
                            const ext = this._mapView.extent.clone();
                            const tiny = ext.width * 0.000001;
                            ext.xmin += tiny;
                            await this._mapView.goTo(ext, { animate: false }).catch(() => {});
                        }
                    }).catch(() => {
                        (this._mapView as any)?.requestRender?.();
                    });
                })
                .catch((err: any) => {
                    if (err?.name !== 'AbortError') {
                        console.error(`Failed to load orthophoto ${entity.originEntityId}:`, err);
                    }
                });
        }
    })
    .catch((err: any) => console.error('Failed to fetch signed URLs:', err));
}
  // Moves the loaded COG layers back above everything else; only when cogAboveShapes is set.
  private keepCogLayersOnTop(): void {
    const map = this._mapView?.map;
    if (!this.cogAboveShapes || !map) return;
    for (const layer of this.cogLayers) {
      if (map.layers.includes(layer)) {
        map.reorder(layer, map.layers.length - 1);
      }
    }
  }

  private clearCogLayers(): void {
    for (const layer of this.cogLayers) {
      this._mapView?.map?.remove(layer);
      try {
        layer.destroy();
      } catch (error) {
        if (error && (error as any).name !== 'AbortError') {
          console.warn('Non-critical COG layer cleanup error:', error);
        }
      }
    }
    this.cogLayers = [];
  }

  private handlePopupAction(event: any): void {
    const actionId = event.action.id;
    const selectedFeature = this._mapView?.popup?.selectedFeature;

    switch (actionId) {
      case 'show-details':
        this.showEntityDetails(selectedFeature);
        break;
      case 'atarMVP':
        this.showAtarMVPDetails(selectedFeature);
        break;
      default:
        console.log('Unknown popup action:', actionId);
    }
  }
  showAtarMVPDetails(feature: any): void {
    const selectedFeatureAttributes =
      this._mapView?.popup?.selectedFeature?.attributes;
    const atarStringTatAtarAndAtar = selectedFeatureAttributes.atar_number;
    const menoraUrlOpenAtarSite = HACHRAZA_DETAILS_URL + '/' + atarStringTatAtarAndAtar;
    window.location.href = menoraUrlOpenAtarSite;
  }

  private showEntityDetails(feature: any): void {
    if (!feature || !feature.attributes?.shape?.shapeData?.entity) {
      return;
    }
    const entity = feature.attributes.shape.shapeData.entity;

    if (entity.entityType === 'photogrammetric_model') {
      this.ngZone.run(() => {
        this.routingService.navigateToEntity(entity.entityId, entity.entityType);
      });
      return;
    }

    if (entity.linkInfo) {
      if (entity.linkInfo.isNewTab && entity.linkInfo.url) {
        window.open(entity.linkInfo.url, '_blank');
      } else if (entity.linkInfo.queryParams) {
        // Wrap navigation in ngZone.run() to ensure Angular change detection works
        this.ngZone.run(() => {
          this.routingService.navigateWithMerge(entity.linkInfo.queryParams);
        });
      }
    } else if (entity.externalUrl || entity.downloadUrl) {
      window.open(entity.externalUrl || entity.downloadUrl, '_blank');
    }
  }
  private initializeShapesAndHighlighting(): void {
    if (this._shapes.length > 0) {
      this.addShapes(this._shapes);
      if (this.selectedEntityId) {
        setTimeout(() => this.updateShapeHighlighting(), 100);
      }
    }
  }

  private initializeSketching(): void {
    if (!this._mapView) {
      console.error('Cannot initialize sketching: map view not ready');
      return;
    }

    try {
      this.sketchingService.initializeSketching(this._mapView);
      this.setupSketchSubscription();
      this.restoreExistingSketchShapes();
      this.sketchingService.ensureSketchLayerOnTop();
    } catch (error) {
      console.error('Error initializing sketching:', error);
    }
  }

  private setupSketchSubscription(): void {
    this.sketchSubscription = this.sketchingService.onShapesChanged.subscribe(
      (shapes: ShapeItem[]) => {
        this.sketchShapesChanged.emit(shapes);
      },
    );
  }

  private restoreExistingSketchShapes(): void {
    if (this.sketchShapes?.length > 0) {
      this.sketchingService.restoreShapes(this.sketchShapes);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleSketchShapesChange(changes);
    this.handleEnableSketchingChange(changes);
    this.handleMapControlsChange(changes);
    this.handleSelectedEntityChange(changes);
  }

  private handleSketchShapesChange(changes: SimpleChanges): void {
    if (
      changes['sketchShapes'] &&
      this.sketchingService.isSketchingInitialized()
    ) {
      const currentShapes = this.sketchingService.getCurrentShapes();
      const newShapes = changes['sketchShapes'].currentValue || [];

      if (this.hasSketchShapesChanged(currentShapes, newShapes)) {
        this.sketchingService.restoreShapes(newShapes);
      }
    }
  }

  private hasSketchShapesChanged(
    currentShapes: ShapeItem[],
    newShapes: ShapeItem[],
  ): boolean {
    return (
      JSON.stringify(currentShapes.map((s) => s.wkt).sort()) !==
      JSON.stringify(newShapes.map((s) => s.wkt).sort())
    );
  }

  private handleEnableSketchingChange(changes: SimpleChanges): void {
    if (
      changes['enableSketching'] &&
      this._mapView &&
      !this.sketchingService.isSketchingInitialized()
    ) {
      if (changes['enableSketching'].currentValue) {
        this.initializeSketching();
        this.sketchingService.ensureSketchLayerOnTop();
      }
    }
  }

  private handleMapControlsChange(changes: SimpleChanges): void {
    if (changes['mapControls'] && this._mapView) {
      this.clearControls();
      this.generateControls();
    }
  }

  private handleSelectedEntityChange(changes: SimpleChanges): void {
    if (
      changes['selectedEntityId'] &&
      this._mapView &&
      this._shapes.length > 0
    ) {
      this.updateShapeHighlighting();

      if (this.selectedEntityId) {
        setTimeout(() => this.zoomToSelectedEntityShape(), 100);
      } else {
        this.closePopupIfVisible();
      }
    }
  }

  private closePopupIfVisible(): void {
    if (this._mapView?.popup?.visible) {
      this._mapView.popup.visible = false;
    }
  }

  async generateControls() {
    if (!this._mapView) {
      return;
    }
    for (const control of this.mapControls) {
      if (control.type === ControlType.BasemapGallery && !this.internalUser) {
        continue;
      }

      // check component added;
      const controlExist = this.dynamicControls.some(
        (a) => a.id === `control-id-${control.type}`,
      );
      if (controlExist) {
        continue;
      }
      const el = this.factory.createControl(control);
      if (el) {
        if (control.expand) {
          const wrappedEl = this.factory.wrapExpand(control.expand, el);
          this.dynamicControls.unshift(wrappedEl);
          this._mapView?.container?.append(wrappedEl);
        } else if (control.toogle) {
          const wrappedEl = this.factory.toggleButton(
            control.toogle,
            el,
            control.tooglePosition,
          );
          this.dynamicControls.unshift(wrappedEl);
          const button = wrappedEl.querySelector('calcite-button');
          if (button) {
            button.addEventListener('click', () => {
              this.toggleControl(control);
            });
          }
          this._mapView?.container?.append(wrappedEl);
        } else {
          this._mapView?.container?.append(el);
        }
        this.dynamicControls.unshift(el);
        if (!control.toogle && typeof el.componentOnReady === 'function') {
          await el.componentOnReady();
        }
        if (control.type === ControlType.BasemapGallery) {
          el.addEventListener('arcgisReady', (event: any) => {
            this.onBasemapGalleryReady(event);
          });
        } else if (control.type === ControlType.LayerList) {
          el.addEventListener('arcgisReady', (event: any) => {
            this.onLayerListReady(event);
          });
          el.addEventListener('arcgisTriggerAction', (event: any) => {
            this.onTriggerAction(event);
          });
        } else if (control.type === ControlType.FullScreen) {
          // Simple fix for full-screen black screen issue
          el.addEventListener('arcgisReady', () => {
            document.addEventListener('fullscreenchange', () => {
              if (this._mapView) {
                setTimeout(
                  () => this._mapView?.goTo(this._mapView.extent),
                  100,
                );
              }
            });
          });
        } else if (control.type === ControlType.Search) {
          el.addEventListener('arcgisReady', (event: any) => {
            this.onSearchReady(event, control);
          });
        }
      }
    }
  }

  clearControls() {
    if (!this._mapView) {
      return;
    }
    for (let el of this.dynamicControls) {
      if (!el) {
        continue;
      }
      el?.remove();
      // Wrap destroy in try-catch to suppress expected AbortErrors from ArcGIS components
      try {
        (el as any)?.destroy?.();
      } catch (error) {
        // Silently handle expected AbortErrors from component cleanup
        if (error && (error as any).name !== 'AbortError') {
          console.warn('Non-critical component cleanup error:', error);
        }
      }
    }
    this.dynamicControls = [];
  }
  handleRightClickOnMapShape(shapes: ShapeItem[]): void {
    const shapesEntity = shapes[0].shapeData.entity;
    this.menuItemsService.setMenuItems(shapesEntity, true, () =>
      this.handleShowInTableMapSelectedEntity([shapesEntity]),
    );
    this.mapMenuItems = this.menuItemsService.getMenuItems(this.currentLang);
  }

  handleShowInTableMapSelectedEntity(entities: any): void {
    this.updateSelectedEntitiesFromMap.emit(entities);
  }

  arcgisViewPointerDown(event: any) {
    event.target.hitTest(event.detail).then((response: any) => {
      const candidate = response.results.find(
        (result: any) => result.graphic && result.graphic.layer,
      );
      const shape = candidate?.graphic?.attributes?.shape;

      if (shape && event.detail.button === 2) {
        this.handleRightClickOnMapShape([shape]);
      }
    });
  }

  private debouncedSetShapes(shapes: ShapeItem[]) {
    if (this.shapesTimeout) {
      clearTimeout(this.shapesTimeout);
    }
    this.shapesTimeout = setTimeout(() => this.addShapes(shapes), 100);
  }

  private addShapes(shapes: ShapeItem[]) {
    if (!this._mapView || shapes.length === 0) return;

    this.clearAllGraphicsLayers();
    const extentGraphics: any[] = [];
    const layerGroups = this.groupShapesByLayer(shapes);

    this.processLayerGroups(layerGroups, extentGraphics);
    this.sketchingService.ensureSketchLayerOnTop();
    this.handleZoomingAfterShapesAdded(extentGraphics);
  }

  private groupShapesByLayer(shapes: ShapeItem[]): Map<string, ShapeItem[]> {
    const layerGroups = new Map<string, ShapeItem[]>();

    for (const shape of shapes) {
      // Skip hidden shapes
      if (shape.hidden) {
        continue;
      }

      const layerName = shape.layerName;

      if (!layerGroups.has(layerName)) {
        layerGroups.set(layerName, []);
      }

      layerGroups.get(layerName)!.push(shape);
    }

    return layerGroups;
  }

  private processLayerGroups(
    layerGroups: Map<string, ShapeItem[]>,
    extentGraphics: any[],
  ): void {
    layerGroups.forEach((shapes, layerName) => {
      this.createSimpleLayer(layerName, shapes, extentGraphics);
    });
  }

  private createSimpleLayer(
    layerName: string,
    shapes: ShapeItem[],
    extentGraphics: any[],
  ): void {
    const layer = new GraphicsLayer({ title: layerName, visible: true });

    this.processShapeItems(shapes, layer, extentGraphics);

    this._mapView?.map?.add(layer);
    this.graphicsLayers.set(layerName, layer);
    this.keepCogLayersOnTop();
  }

  private processShapeItems(
    shapeItems: ShapeItem[],
    layer: GraphicsLayer,
    extentGraphics: any[],
  ): void {
    shapeItems.forEach((shape) => {
      // Skip hidden shapes when adding graphics
      if (shape.hidden) {
        return;
      }

      this.applyHighlighting(shape);
      const graphic = this.graphicHelper.convertToGrapic(shape);

      if (graphic) {
        if (this.shouldIncludeInExtent(shape)) {
          extentGraphics.push(graphic);
        }
        layer.add(graphic);
      }
    });
  }

  private applyHighlighting(shape: ShapeItem): void {
    let shouldHighlight = false;

    if (this.selectedEntityId) {
      if (shape.shapeData?.entity?.entityId === this.selectedEntityId) {
        shouldHighlight = true;
      } else if (
        shape.shapeData?.entities &&
        Array.isArray(shape.shapeData.entities)
      ) {
        shouldHighlight = shape.shapeData.entities.some(
          (entity: any) => entity.entityId === this.selectedEntityId,
        );
      }
    }

    shape.highlighted = shouldHighlight;
  }

  private shouldIncludeInExtent(shape: ShapeItem): boolean {
    return this.extentAll || shape.isExtent === true;
  }

  private handleZoomingAfterShapesAdded(extentGraphics: any[]): void {
    if (this.selectedEntityId && this._shapes.length > 0) {
      this.zoomToSelectedEntityShape();
    } else if (extentGraphics.length > 0) {
      this.zoomToExtentGraphics(extentGraphics);
    }
  }

  private zoomToExtentGraphics(extentGraphics: any[]): void {
    const combinedExtent = this.combineGraphicsExtent(extentGraphics);
    const padding = { top: 20, bottom: 20, left: 20, right: 20 };

    if (this._mapView?.ready) {
      this._mapView.goTo(combinedExtent, { padding } as any);
    } else {
      this._mapView?.when(() => {
        this._mapView?.goTo(combinedExtent, { padding } as any);
      });
    }
  }

  private toggleControl(control: any) {}

  private combineGraphicsExtent(selectedGraphics: any[]) {
    if (selectedGraphics.length === 0) return;

    let combinedExtent = this.graphicHelper
      .getExtent(selectedGraphics[0].geometry)
      .clone();
    for (let i = 1; i < selectedGraphics.length; i++) {
      combinedExtent = combinedExtent.union(
        this.graphicHelper.getExtent(selectedGraphics[i].geometry),
      );
    }
    return combinedExtent.clone();
  }

  private clearAllGraphicsLayers() {
    for (const gl of this.graphicsLayers.values()) {
      gl.removeAll();
      this._mapView?.map?.remove(gl);
    }
    this.graphicsLayers.clear();
  }

  private onLayerListReady(event: any) {
    const layerList = event.target;
    this.lifecycleHooks.onLayerListReady?.(layerList);

    if (this.enableSketching) {
      this.sketchingService.ensureSketchLayerOnTop();
    }
  }

  private onBasemapGalleryReady(event: any) {
    const basemapGallery = event.target;
    this.lifecycleHooks.onBasemapGalleryReady?.(basemapGallery);
  }
  private onSearchReady(event: any, control: MapControlItem) {
    const gushAndParcelLayer = this._mapView?.map?.findLayerById(
      'gush-and-parcel-layer',
    );

    if (!gushAndParcelLayer) {
      this.retrySearchReady(event, control);
      return;
    }

    gushAndParcelLayer
      .load()
      .then(() => this.configureSearchWidget(event.target, gushAndParcelLayer))
      .catch((error: any) =>
        console.error(
          'Failed to load gush-and-parcel-layer for search:',
          error,
        ),
      );
  }

  private retrySearchReady(event: any, control: MapControlItem): void {
    setTimeout(() => {
      const retryLayer = this._mapView?.map?.findLayerById(
        'gush-and-parcel-layer',
      );
      if (retryLayer) {
        this.onSearchReady(event, control);
      } else {
        console.error('[onSearchReady] gush-and-parcel-layer not found');
      }
    }, 500);
  }

  private configureSearchWidget(
    searchWidget: any,
    gushAndParcelLayer: any,
  ): void {
    const fields = gushAndParcelLayer.fields;
    const { gushFieldName, parcelFieldName } =
      this.gushParcelSearchService.detectGushParcelFields(fields);

    const gushParcelSource = this.createGushParcelSearchSource(
      gushAndParcelLayer,
      gushFieldName,
      parcelFieldName,
    );
    const addressSource = this.createAddressSearchSource();

    this.recreateSearchWidget(searchWidget, [gushParcelSource, addressSource]);

    // Wait for web component to be ready before adding event listeners
    this.searchWidgetElement?.addEventListener(
      'arcgisReady',
      () => {
        this.setupSearchEventListeners(
          this.searchWidgetElement,
          gushAndParcelLayer,
        );
      },
      { once: true },
    );

    this.autoExpandSearchIfRequested();
  }

  private createGushParcelSearchSource(
    layer: any,
    gushFieldName: string,
    parcelFieldName: string,
  ): LayerSearchSource {
    const currentLang = this.languageService.currentLanguage.getValue() as
      | 'he'
      | 'en';
    const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');

    return new LayerSearchSource({
      layer: layer,
      searchFields: [gushFieldName, parcelFieldName],
      displayField: layer.displayField || gushFieldName || 'OBJECTID',
      placeholder: this.translate.instant('iaa.map.search-gush-parcel'),
      name: layerTitle,
      exactMatch: false,
      outFields: ['*'],
      maxResults: 6,
      maxSuggestions: 6,
      suggestionsEnabled: true,
      minSuggestCharacters: 1,
      popupTemplate: createGushParcelPopupTemplate(
        layerTitle,
        currentLang,
        this.internalUser,
      ) as any,
      getSuggestions: (params: any) =>
        this.gushParcelSearchService.getSuggestions(
          params,
          layer,
          gushFieldName,
          parcelFieldName,
        ),
      getResults: (params: any) =>
        this.gushParcelSearchService.getResults(
          params,
          layer,
          gushFieldName,
          parcelFieldName,
        ),
    });
  }

  private createAddressSearchSource(): LocatorSearchSource {
    return new LocatorSearchSource({
      url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer',
      placeholder: this.translate.instant('iaa.map.search-address'),
      name: this.translate.instant('iaa.map.addressSearch'),
      countryCode: 'IL',
      maxResults: 6,
      maxSuggestions: 6,
      suggestionsEnabled: true,
      minSuggestCharacters: 3,
    });
  }

  private recreateSearchWidget(searchWidget: any, sources: any[]): void {
    const parent = searchWidget.parentElement;
    const isWrappedInExpand =
      parent?.tagName?.toLowerCase() === 'arcgis-expand';

    searchWidget.remove();

    const newSearchEl = document.createElement('arcgis-search') as any;
    newSearchEl.id = `control-id-${ControlType.Search}`;

    if (!isWrappedInExpand) {
      newSearchEl.position = searchWidget.position;
    }

    // Set view FIRST - critical for widget to function
    newSearchEl.view = this._mapView;
    newSearchEl.includeDefaultSourcesDisabled = true;
    newSearchEl.sources = sources;
    newSearchEl.activeSourceIndex = 0;
    newSearchEl.locationDisabled = true;

    parent?.appendChild(newSearchEl);

    this.searchWidgetElement = newSearchEl;
    this.searchExpandElement = isWrappedInExpand ? parent : null;
  }

  private setupSearchEventListeners(searchElement: any, layer: any): void {
    searchElement.addEventListener('search-start', () => {
      layer.visible = true;
    });

    searchElement.addEventListener('arcgisSelectResult', (e: any) => {
      this.highlightSearchResult(e.detail?.result);
      this.enableHachrazaLayerOnGushParcelFound(e.detail?.result);
    });

    searchElement.addEventListener('arcgisSearchClear', () => {
      this._mapView?.graphics.removeAll();
    });
  }

  private highlightSearchResult(result: any): void {
    if (!result?.feature) return;

    this._mapView?.graphics.removeAll();

    const highlightGraphic = result.feature.clone();
    highlightGraphic.symbol = {
      type: 'simple-fill',
      color: [179, 229, 252, 0.4],
      outline: {
        color: [0, 255, 255, 1],
        width: 3,
      },
    };

    this._mapView?.graphics.add(highlightGraphic);
  }

  private enableHachrazaLayerOnGushParcelFound(result: any): void {
    if (!result?.feature) return;
    const attributes = result.feature.attributes;
    const hasGushParcel =
      attributes &&
      Object.keys(attributes).some(
        (key) =>
          key.toUpperCase().includes('GUSH') ||
          key.toUpperCase().includes('PARCEL') ||
          key.toUpperCase().includes('HELKA') ||
          key.toUpperCase().includes('CHELKA'),
      );

    if (hasGushParcel) {
      const gushParcelLayer = this._mapView?.map?.findLayerById(
        'gush-and-parcel-layer',
      );
      if (gushParcelLayer) {
        gushParcelLayer.visible = true;
      }

      const hachrazaLayer = this._mapView?.map?.findLayerById('hacrazot-layer');
      if (hachrazaLayer) {
        hachrazaLayer.visible = true;

        // Ensure hachraza layer is above gush-parcel layer
        if (gushParcelLayer && this._mapView?.map) {
          const gushParcelIndex =
            this._mapView.map.layers.indexOf(gushParcelLayer);
          const hachrazaIndex = this._mapView.map.layers.indexOf(hachrazaLayer);

          if (hachrazaIndex < gushParcelIndex) {
            this._mapView.map.reorder(hachrazaLayer, gushParcelIndex + 1);
          }
        }
      }
    }
  }

  private autoExpandSearchIfRequested(): void {
    if (!this.autoExpandSearch) return;

    setTimeout(() => {
      if (this.searchExpandElement) {
        this.searchExpandElement.expanded = true;
      } else if (this.searchWidgetElement) {
        const searchInput = this.searchWidgetElement.querySelector('input');
        searchInput?.focus();
      }
    }, 500);
  }

  private onTriggerAction(event: any) {
    const action = event.detail;
    this.lifecycleHooks.onHandleActionClicks?.(action);
  }

  private updateContextMenu() {
    if (this.clickedShape) {
      this.mapMenuItems = this.createShapeContextMenuItems(this.clickedShape);
    } else {
      this.mapMenuItems = this.menuItemsService.getMenuItems(this.currentLang);
    }
  }

  /**
   * Update layer titles when language changes
   */
  private updateLayerTitles(): void {
    if (!this._mapView) {
      return;
    }

    // Update our own graphics layers (query results)
    if (this._shapes.length > 0) {
      // Group shapes by layer name to update titles
      const layerGroups = new Map<string, ShapeItem[]>();
      this._shapes.forEach((shape) => {
        const layerName = shape.layerName;
        if (!layerGroups.has(layerName)) {
          layerGroups.set(layerName, []);
        }
        layerGroups.get(layerName)!.push(shape);
      });

      // Update layer titles
      layerGroups.forEach((shapes, layerName) => {
        const layer = this.graphicsLayers.get(layerName);
        if (layer) {
          // Update layer title with translated text
          const translatedLayerName =
            this.translate.instant(shapes[0].layerName) || shapes[0].layerName;
          layer.title = translatedLayerName;
        }
      });
    }

    // Update base map layers (non-query result layers)
    if (this._mapView.map && this._mapView.map.allLayers) {
      this.updateBasemapLayerTitles();
    }
  }
  private updateBasemapLayerTitles(): void {
    const layerIdToTranslationKey = new Map([
      ['hacrazot', 'iaa.map.hachrazaLayer'],
      ['geo-areas', 'iaa.map.geoAreasLayer'],
      ['fire-zones', 'iaa.map.fireZonesLayer'],
      ['gush-and-parcel', 'iaa.map.gushAndParcelLayer'],
      ['heritage-sites', 'iaa.map.heritageSitesLayer'],
      ['ba-layer', 'iaa.map.baLayer'],
      ['bdikat-tochniyot', 'iaa.map.bdikatTochniyotLayer'],
      ['hb-group', 'iaa.map.hbGroupLayer'],
      ['boundary-center', 'iaa.map.hbBoundaryCenterPolygons'],
      ['hb-polygon', 'iaa.map.hbPolygonGlobal'],
      ['hb-line', 'iaa.map.hbLineGlobal'],
      ['hb-points', 'iaa.map.hbPointsGlobal'],
      // ['hb-point-global-with-z', 'iaa.map.hbPointGlobalWithZ'],
      ['boundary-survery', 'iaa.map.boundarySurveryGlobal'],
      // ['survery-global-polygons', 'iaa.map.surveryGlobalPolygons'],
      ['survery-global-lines', 'iaa.map.surveryGlobalLines'],
      // ['survery-global-points', 'iaa.map.surveryGlobalPoints'],
      ['survey-main-caves', 'iaa.map.surveyMainCaves'],
      // ['survery-global-polygon-public', 'iaa.map.surveryGlobalPolygonPublic'],
      // ['survery-global-point-public', 'iaa.map.surveryGlobalPointPublic']
    ]);

    this._mapView!.map!.allLayers.forEach((layer: any) => {
      if (layer.type === 'graphics' || layer.id === 'sketch-layer') return;

      this.updateLayerTitle(layer, layerIdToTranslationKey);

      // Handle group layers
      if (layer.layers?.length > 0) {
        layer.layers.forEach((subLayer: any) => {
          this.updateLayerTitle(subLayer, layerIdToTranslationKey);
        });
      }
    });
  }

  private updateLayerTitle(
    layer: any,
    layerIdToTranslationKey: Map<string, string>,
  ): void {
    if (!layer.title || !layer.id) return;

    for (const [idFragment, translationKey] of layerIdToTranslationKey) {
      if (layer.id.includes(idFragment)) {
        const translatedText = this.translate.instant(translationKey);
        if (translatedText && translatedText !== translationKey) {
          layer.title = translatedText;
          break;
        }
      }
    }
  }

  /**
   * Update popup templates for shapes based on current language
   */
  private updateShapesPopupTemplates(): void {
    if (!this._shapes || this._shapes.length === 0) {
      return;
    }

    this._shapes.forEach((shape) => {
      if (shape.shapeData) {
        // Recreate popup template with current language
        if (
          shape.shapeData.entities &&
          Array.isArray(shape.shapeData.entities) &&
          shape.shapeData.entities.length > 1
        ) {
          // Multiple entities - create redundant shapes popup
          const viewText = this.currentLang === 'he' ? 'לצפייה' : 'View';
          const resultsText = this.currentLang === 'he' ? 'תוצאות' : 'results';

          const entityTypeCount: { [key: string]: number } = {};
          shape.shapeData.entities.forEach((entity: any) => {
            const entityTypeName = entity.entityTypeName || entity.entityType;
            entityTypeCount[entityTypeName] =
              (entityTypeCount[entityTypeName] || 0) + 1;
          });

          let totalCount = Object.values(entityTypeCount).reduce(
            (sum, count) => sum + count,
            0,
          );
          const space = ' ';

          // Get the translated entity type name based on current language
          const translatedEntityTypeName =
            typeof shape.shapeData.entity.entityTypeName === 'string'
              ? shape.shapeData.entity.entityTypeName
              : this.currentLang === 'he'
                ? shape.shapeData.entity.entityTypeName?.hebrew ||
                  shape.shapeData.entity.entityTypeName?.english ||
                  ''
                : shape.shapeData.entity.entityTypeName?.english ||
                  shape.shapeData.entity.entityTypeName?.hebrew ||
                  '';

          shape.popupTemplate = {
            title: `<div id="popup" dir="rtl">${translatedEntityTypeName}${space}-${space}${totalCount}${space}${resultsText} </div>`,
            content: [
              {
                type: 'text',
                text: `<div id="popup"><h4></h4></br>${Object.entries(
                  entityTypeCount,
                )
                  .map(
                    ([entityType, count]) =>
                      `<p>${entityType}: ${count}${space}${resultsText} </p>`,
                  )
                  .join('')}</div></br>`,
              },
            ],
          };
        } else if (shape.shapeData.entity) {
          // Single entity
          const viewText = this.currentLang === 'he' ? 'לצפייה' : 'View';
          const detailsButtonText =
            this.currentLang === 'he' ? 'פרטים מלאים' : 'Full Details';
          const entity = shape.shapeData.entity;

          // Handle multilingual title
          const translated_title =
            typeof entity.title === 'string'
              ? entity.title
              : this.currentLang === 'he'
                ? entity.title?.hebrew || entity.title?.english || ''
                : entity.title?.english || entity.title?.hebrew || '';

          // Handle multilingual description
          const translated_description =
            typeof entity.description === 'string'
              ? entity.description
              : this.currentLang === 'he'
                ? entity.description?.hebrew ||
                  entity.description?.english ||
                  ''
                : entity.description?.english ||
                  entity.description?.hebrew ||
                  '';

          // Build actions array - add show-details button if entity has linkInfo or URLs
          const actions: any[] = [];
          if ((entity.linkInfo || entity.externalUrl || entity.downloadUrl) && entity.entityType !== 'orthophoto' && entity.entityType !== 'orthophotos') {
            actions.push({
              title: detailsButtonText,
              id: 'show-details',
              className: 'esri-icon-review',
            });
          }

          shape.popupTemplate = {
            title: `<div id="popup" style="justify-content:right !important;">${translated_title}</div>`,
            content: [
              {
                type: 'text',
                text: `
							<div id="popup">
								${
                  translated_description
                    ? `<strong>${translated_description}</strong><br/>`
                    : ``
                }
							</div>
						`,
              },
              {
                type: 'media',
				mediaInfos: entity.entityType === 'orthophotos' ? [] : [{
					type: 'image',
					value: { sourceURL: `${entity.imageUrl}` }
				}]
              },
            ],
            actions: actions,
          };
        }
      }
    });
  }

  /**
   * Refresh an open popup with updated content after language change using WKT to find the graphic
   */
  private refreshOpenPopupByWKT(popupLocation: any, wkt: string): void {
    if (!this._mapView?.popup || !popupLocation || !wkt) {
      return;
    }

    // Find the graphic with the matching WKT
    let foundGraphic: any = null;

    this.graphicsLayers.forEach((layer) => {
      if (!foundGraphic && (layer as any).graphics) {
        (layer as any).graphics.forEach((graphic: any) => {
          if (!foundGraphic && graphic.attributes?.shape?.wkt === wkt) {
            foundGraphic = graphic;
          }
        });
      }
    });

    if (foundGraphic && this._mapView?.popup) {
      // Find all graphics with the same geometry for popup navigation
      const allGraphicsWithSameGeometry =
        this.findAllGraphicsWithSameGeometry(foundGraphic);

      // Update the popup with the new graphics that have updated popup templates
      this._mapView.popup.features = allGraphicsWithSameGeometry;
      this._mapView.popup.location = popupLocation;
      this._mapView.popup.visible = true;
    } else {
      console.warn(
        'Could not find graphic with WKT to refresh popup after language change',
      );
    }
  }

  private createShapeContextMenuItems(shape: ShapeItem): any[] {
    const items = [
      {
        label: this.currentLang === 'he' ? 'פרטי הצורה' : 'Shape Details',
        icon: 'pi pi-info-circle',
        command: () => this.showShapeDetails(shape),
      },
      {
        separator: true,
      },
    ];

    // Add shape-specific information
    if (shape.attributes) {
      Object.keys(shape.attributes).forEach((key) => {
        if (key !== 'shape' && shape.attributes[key]) {
          items.push({
            label: `${key}: ${shape.attributes[key]}`,
            icon: 'pi pi-tag',
            command: () => {},
          });
        }
      });
    }

    items.push({
      label:
        this.translate.instant('iaa.map.mapLayer') + `: ${shape.layerName}`,
      icon: 'pi pi-layers',
      command: () => {},
    });

    items.push(
      { separator: true },
      {
        label: this.translate.instant('iaa.map.copyLocation'),
        icon: 'pi pi-copy',
        command: () => this.copyShapeLocation(shape),
      },
      {
        label: this.translate.instant('iaa.map.centerOnShape'),
        icon: 'pi pi-crosshairs',
        command: () => this.centerOnShape(shape),
      },
    );

    return items;
  }

  private showShapeDetails(shape: ShapeItem) {}

  private copyShapeLocation(shape: ShapeItem) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shape.wkt);
    }
  }

  private centerOnShape(shape: ShapeItem) {
    if (this._mapView && shape.wkt) {
      const graphic = this.graphicHelper.convertToGrapic(shape);
      if (graphic) {
        const extent = this.graphicHelper.getExtent(graphic.geometry);
        this._mapView.goTo(extent);
      }
    }
  }

  private updateShapeHighlighting() {
    if (!this._shapes.length || !this._mapView) {
      return;
    }

    // Update highlighting for all shapes based on selectedEntityId
    this._shapes.forEach((shape) => {
      let shouldHighlight = false;
      if (this.selectedEntityId) {
        // Check if the selected entity is the primary entity for this shape
        if (shape.shapeData?.entity?.entityId === this.selectedEntityId) {
          shouldHighlight = true;
        }
        // Check if the selected entity is in the entities array (for grouped shapes)
        else if (
          shape.shapeData?.entities &&
          Array.isArray(shape.shapeData.entities)
        ) {
          shouldHighlight = shape.shapeData.entities.some(
            (entity: any) => entity.entityId === this.selectedEntityId,
          );
        }
      }

      shape.highlighted = shouldHighlight;
    });

    // Re-add shapes to update the graphics with new highlighting
    this.addShapesWithHighlightingOnly(this._shapes);
  }

  private addShapesWithHighlightingOnly(shapes: ShapeItem[]) {
    if (this._mapView) {
      // Store current layer visibility states before clearing
      const layerVisibilityMap = new Map<string, boolean>();
      this.graphicsLayers.forEach((layer, layerName) => {
        layerVisibilityMap.set(layerName, layer.visible);
      });

      this.clearAllGraphicsLayers();

      // Get the sketch layer to maintain its position
      const sketchLayer = this.sketchingService.getSketchLayer();

      // Group shapes by layer name
      const layerGroups = new Map<string, ShapeItem[]>();

      for (const shape of shapes) {
        if (shape.hidden) {
          continue;
        }
        const layerName = shape.layerName;

        if (!layerGroups.has(layerName)) {
          layerGroups.set(layerName, []);
        }

        layerGroups.get(layerName)!.push(shape);
      }

      layerGroups.forEach((shapeItems, layerName) => {
        // Restore previous visibility state, or default to true if this is a new layer
        const wasVisible = layerVisibilityMap.has(layerName)
          ? layerVisibilityMap.get(layerName)
          : true;

        const layer = new GraphicsLayer({
          title: layerName,
          visible: wasVisible,
        });

        shapeItems.forEach((shape) => {
          // Apply highlighting if this shape corresponds to the selected entity
          let shouldHighlight = false;
          if (this.selectedEntityId) {
            // Check if the selected entity is the primary entity for this shape
            if (shape.shapeData?.entity?.entityId === this.selectedEntityId) {
              shouldHighlight = true;
            }
            // Check if the selected entity is in the entities array (for grouped shapes)
            else if (
              shape.shapeData?.entities &&
              Array.isArray(shape.shapeData.entities)
            ) {
              shouldHighlight = shape.shapeData.entities.some(
                (entity: any) => entity.entityId === this.selectedEntityId,
              );
            }
          }

          if (shouldHighlight) {
            shape.highlighted = true;
          } else {
            shape.highlighted = false;
          }

          const graphic = this.graphicHelper.convertToGrapic(shape);
          if (graphic) {
            layer.add(graphic);
          }
        });

        this._mapView?.map && this._mapView.map.add(layer);
        this.graphicsLayers.set(layerName, layer);
      });
      this.keepCogLayersOnTop();
      // Ensure sketch layer stays at the top after adding new shapes
      this.sketchingService.ensureSketchLayerOnTop();
    }
  }

  private zoomToSelectedEntityShape() {
    if (!this._mapView || !this.selectedEntityId || !this._shapes.length) {
      return;
    }

    const selectedShape = this._shapes.find((shape) => {
      // Check if the entity in this shape matches the selected entity (primary entity)
      if (
        shape.shapeData?.entity?.entityId === this.selectedEntityId ||
        shape.shapeData?.entity?.entityId === Number(this.selectedEntityId)
      ) {
        return true;
      }
      // Also check if the selected entity is in the entities array (for grouped shapes)
      if (
        shape.shapeData?.entities &&
        Array.isArray(shape.shapeData.entities)
      ) {
        return shape.shapeData.entities.some(
          (entity: any) =>
            entity.entityId === this.selectedEntityId ||
            entity.entityId === Number(this.selectedEntityId),
        );
      }
      return false;
    });

    if (selectedShape) {
      const graphic = this.graphicHelper.convertToGrapic(selectedShape);

      if (graphic) {
        const extent = this.keepZoomOnSelect
          ? this.graphicHelper.getExtent(graphic.geometry).center
          : this.graphicHelper.getExtent(graphic.geometry);

        const handleZoomComplete = () => {
          setTimeout(() => {
            this.openPopupForSelectedShape(graphic);
          }, 300);
        };

        if (this._mapView?.ready) {
          this._mapView
            .goTo(extent, {
              padding: {
                top: 20,
                bottom: 20,
                left: 20,
                right: 20,
              },
            } as any)
            .then(handleZoomComplete)
            .catch((error) => {
              console.warn('Zoom failed, but still opening popup:', error);
              handleZoomComplete();
            });
        } else {
          this._mapView?.when(() => {
            this._mapView
              ?.goTo(extent, {
                padding: {
                  top: 20,
                  bottom: 20,
                  left: 20,
                  right: 20,
                },
              } as any)
              .then(handleZoomComplete)
              .catch((error) => {
                console.warn('Zoom failed, but still opening popup:', error);
                handleZoomComplete();
              });
          });
        }
      }
    }
  }

  private openPopupForSelectedShape(graphic: any): void {
    if (!this._mapView?.popup || !graphic) {
      console.warn('Cannot open popup: mapView or graphic missing', {
        mapView: !!this._mapView,
        popup: !!this._mapView?.popup,
        graphic: !!graphic,
      });
      return;
    }

    if (!this.popupEnabled) {
      console.warn('Popup is disabled, enabling it now');
      this.popupEnabled = true;
    }

    try {
      // Close any existing popup first
      if (this._mapView.popup.visible) {
        this._mapView.popup.visible = false;
      }

      // Find all graphics with the same geometry (WKT) for popup navigation
      let allGraphicsWithSameGeometry =
        this.findAllGraphicsWithSameGeometry(graphic);

      console.log('Opening popup for graphic', {
        entityId: graphic.attributes?.shape?.shapeData?.entity?.entityId,
        matchingGraphics: allGraphicsWithSameGeometry.length,
      });

      // Ensure the selected graphic appears first in the popup
      const selectedEntityId =
        graphic.attributes?.shape?.shapeData?.entity?.entityId;
      if (selectedEntityId && allGraphicsWithSameGeometry.length > 1) {
        allGraphicsWithSameGeometry = allGraphicsWithSameGeometry.sort(
          (a, b) => {
            const aEntityId = a.attributes?.shape?.shapeData?.entity?.entityId;
            const bEntityId = b.attributes?.shape?.shapeData?.entity?.entityId;
            if (aEntityId === selectedEntityId) return -1;
            if (bEntityId === selectedEntityId) return 1;
            return 0;
          },
        );
      }

      // Determine popup location based on geometry type
      let popupLocation = null;
      if (graphic.geometry) {
        if (graphic.geometry.type === 'point') {
          popupLocation = graphic.geometry;
        } else if (
          graphic.geometry.type === 'polygon' &&
          graphic.geometry.centroid
        ) {
          popupLocation = graphic.geometry.centroid;
        } else if (graphic.geometry.extent && graphic.geometry.extent.center) {
          popupLocation = graphic.geometry.extent.center;
        }
      }

      // Open popup by setting features and location
      if (popupLocation) {
        this._mapView.popup.features = allGraphicsWithSameGeometry; // Multiple features enable navigation
        this._mapView.popup.location = popupLocation;
        this._mapView.popup.visible = true;
        console.log('Popup opened successfully', {
          location: popupLocation,
          visible: this._mapView.popup.visible,
        });
      } else {
        console.warn('No popup location found', {
          geometryType: graphic.geometry?.type,
        });
      }
    } catch (error) {
      console.error('Error opening popup:', error);
    }
  }

  /**
   * Find all graphics that share the same geometry (WKT) as the given graphic
   */
  private findAllGraphicsWithSameGeometry(targetGraphic: any): any[] {
    if (!targetGraphic?.attributes?.shape?.wkt) {
      return [targetGraphic];
    }

    const targetWKT = targetGraphic.attributes.shape.wkt;
    const matchingGraphics: any[] = [];

    this.graphicsLayers.forEach((layer) => {
      (layer as any).graphics.forEach((graphic: any) => {
        const graphicWKT = graphic.attributes?.shape?.wkt;
        if (graphicWKT === targetWKT) {
          matchingGraphics.push(graphic);
        }
      });
    });

    return matchingGraphics.length > 0 ? matchingGraphics : [targetGraphic];
  }

  public clearSketchShapes(): void {
    if (this.sketchingService.isSketchingInitialized()) {
      this.sketchingService.clearShapes();
    }
  }

  public getCurrentSketchShapes(): ShapeItem[] {
    return this.sketchingService.getCurrentShapes();
  }

  public handleDialogReopen(): void {
    if (this.sketchingService.isSketchingInitialized()) {
      this.sketchingService.handleDialogReopen();
    }
  }

  private setEsriDirectionAndLocale() {
    if (this._mapView && this.currentLang === 'he') {
      const mapDiv = this._mapView.container;
      if (mapDiv && mapDiv.setAttribute) {
        mapDiv.setAttribute('dir', 'rtl');
      }
      if (this._mapView.popup) {
        (this._mapView.popup as any).direction = 'rtl';
        (this._mapView.popup as any).locale = 'he';
      }
      // Set direction/locale for all widgets if possible
      const widgets = (this._mapView as any).ui?.components || [];
      widgets.forEach((widget: any) => {
        if (widget && typeof widget === 'object') {
          widget.direction = 'rtl';
          widget.locale = 'he';
        }
      });
    }
  }
}
